import { initBrowseSpaces, withBrowseSpacesLink } from '@/modules/misc/common/navbar/components/browseSpaces';
import { createConciergeEntry } from '@/modules/misc/common/navbar/components/concierge';
import { createNavbar } from '@/modules/misc/common/navbar/components/navbar';
import { buildTrackedElementValue } from '@/modules/misc/common/navbar/components/navbarLinks';
import { createVCAnnouncementsButton } from '@/modules/misc/common/navbar/components/vc-announcements';
import { createVCAvatarDropdown } from '@/modules/misc/common/navbar/components/vc-navigation-dropdown';
import breakpointConfig from '@/modules/misc/common/navbar/linksPosition/breakpointConfig.json';
import { isTabBarVisible, withAvatarMenuMobileBreakpoint } from '@/modules/misc/common/navbar/utils/avatarMenuMode.js';
import syncAppBadge from '@/utils/app-badge';
import rememberHomeScreenApp from '@/utils/home-screen-app';

import config from './config.json';
import configVC from './config-vc.json';
import { enhanceLinksWithData } from './utils/data.js';
import { filterLinks } from './utils/filtering.js';
import { addResizeListener } from './utils/resizeListener.js';
import { getTrackingSection } from './utils/trackingSection.js';
import { updateFavoriteRooms } from './utils/updateFavoriteRooms.js';
import { updateLeadHubCount } from './utils/updateLeadHubCount.js';
import { updateNumUnreadEnquiries } from './utils/updateNumUnreadEnquiries.js';
import { updateProfilePicture } from './utils/updateProfilePicture.js';
import { updateUserName } from './utils/updateUserName.js';

const navbarBreakpointConfig = {
  ...breakpointConfig,
  ...(window._NAVBAR_BREAKPOINT_CONFIG || {}),
};

async function getUser() {
  return App.es6.shared.user_data.getUser();
}

function addLogoTracking(user) {
  const logo = document.querySelector('.js-header-logo');

  if (!logo) {
    return;
  }

  logo.addEventListener('click', () => {
    App.track.event('navbar_interaction', {
      section: getTrackingSection(user.type),
      element: 'tagvenue_logo',
      interaction: 'click',
    });
  });
}

function handleLogoRedirect(user) {
  const logo = document.querySelector('.js-header-logo');

  if (!logo) {
    return;
  }

  logo.addEventListener('click', (e) => {
    if (user.type === 'manager') {
      e.preventDefault();
      window.location.href = '/venue-cabinet/enquiries/all';
    }
  });
}

const trackedVisibleLinkNames = new Set();

// @todo TM-7783 remove after the business account MVP is validated.
// Opt-in via options.tracking.trackVisible so existing links (which have never
// fired a "visible" event) aren't affected; deduplicated per link name since main()
// re-runs on resize breakpoint changes and loginState:change.
function trackVisibleLinks(links) {
  links.forEach(link => {
    const tracking = link.options?.tracking;
    if (!tracking?.trackVisible || trackedVisibleLinkNames.has(link.name)) {
      return;
    }
    trackedVisibleLinkNames.add(link.name);

    App.track.event('navbar_interaction', {
      section: tracking.section,
      element: tracking.element,
      ...buildTrackedElementValue(link.options),
      interaction: 'visible',
    });
  });
}

// Below the tab bar the avatar owns the whole navigation, so the dropdown gets the same manager link
// set the public navbar collapses into it. Once the tabs are on screen it drops back to the account
// tail - repeating them would be a second copy of what the viewer can already see.
function resolveVCDropdownLinks({ links, user }) {
  if (user.type !== 'manager') {
    return configVC.links || [];
  }

  if (!isTabBarVisible()) {
    return links;
  }

  return enhanceLinksWithData({ links: configVC.links || [], user });
}

// Exported for the wiring spec: the order of the render and repaint calls below is the thing under
// test, and it cannot be observed from any single one of them.
export async function main() {
  const user = await getUser();
  const numUnreadThreads = parseInt(user.unreadEnquiries || 0, 10);
  const showDot = numUnreadThreads > 0;

  let links = config[user.type];
  links = filterLinks({ links, user });
  trackVisibleLinks(links);
  links = enhanceLinksWithData({ links, user });
  // Ahead of the layout: whether the viewer is offered this menu decides the width at which the avatar
  // menu turns mobile.
  initBrowseSpaces(user);
  links = withBrowseSpacesLink({ links, user });
  createNavbar({ links, user, showDot });
  createConciergeEntry(user);

  createVCAvatarDropdown({
    links: resolveVCDropdownLinks({ links, user }),
    user,
    menuFooter: window.__VC_NAVBAR_MOBILE_EXTRAS?.footer || null,
  });

  // After both render paths, never between them: the pill this repaints is built by whichever of
  // them owns the navbar here, and inside the Venue Cabinet that is the dropdown.
  if (user.type === 'manager') {
    updateLeadHubCount();
    syncAppBadge(user.unreadEnquiriesCount);
    rememberHomeScreenApp();
  }

  createVCAnnouncementsButton();
  addLogoTracking(user);
  handleLogoRedirect(user);
}

function addListeners() {
  addResizeListener(
    withAvatarMenuMobileBreakpoint(navbarBreakpointConfig.breakpoints),
    main,
  );

  $.subscribe('enquires:updateCount', () => {
    updateNumUnreadEnquiries();
  });

  $.subscribe('wishList:change', savedRoomsCount => {
    updateFavoriteRooms(savedRoomsCount);
  });

  $.subscribe('profilePicture:change', url => {
    updateProfilePicture(url);
  });

  $.subscribe('userName:change', obj => {
    updateUserName(obj);
  });

  // eslint-disable-next-line no-use-before-define
  $.subscribe('loginState:change', () => reloadNavbar());
}

function loadNavbar() {
  const interval = setInterval(() => {
    if (window?.App?.es6?.shared?.user_data) {
      clearInterval(interval);
      main().then(() => {
        addListeners();
      });
    }
  }, 100);
}

export default loadNavbar;

export function reloadNavbar() {
  window.__TGV_USER_DATA_LOAD(() => {
    loadNavbar();
  });
}
